/**
 * Unified Morph SDK Client
 * 
 * Provides access to all Morph tools through a single interface
 * 
 * @example
 * ```typescript
 * import { MorphClient } from '@morphllm/morphsdk';
 * 
 * const morph = new MorphClient({ 
 *   apiKey: process.env.MORPH_API_KEY,
 *   debug: true,
 *   timeout: 60000
 * });
 * 
 * // Direct execution
 * await morph.fastApply.execute({ target_filepath: 'src/index.ts', ... });
 * await morph.warpGrep.execute({ query: 'Find auth', repoRoot: '.' });
 * await morph.codebaseSearch.search({ query: 'auth logic', repoId: 'x' });
 * 
 * // Tool creation for AI agents (API key inherited)
 * const grepTool = morph.openai.createWarpGrepTool({ repoRoot: '.' });
 * const searchTool = morph.anthropic.createCodebaseSearchTool({ repoId: 'x' });
 * const editTool = morph.vercel.createEditFileTool({ baseDir: './src' });
 * 
 * // Use tools with OpenAI
 * const response = await openai.chat.completions.create({
 *   model: 'gpt-4o',
 *   tools: [grepTool],
 *   messages: [{ role: 'user', content: 'Find the bug' }]
 * });
 * ```
 */

import type { RetryConfig } from './tools/utils/resilience.js';
import { MorphAPIClient } from './core/client.js';
import { logger } from './logger.js';
import { FastApplyClient } from './tools/fastapply/core.js';
import { CodebaseSearchClient } from './tools/codebase_search/core.js';
import { BrowserClient } from './tools/browser/core.js';
import { WarpGrepClient } from './tools/warp_grep/client.js';
import { GitHubClient } from './tools/github/core.js';
import { MorphGit } from './git/index.js';
import { CompactClient } from './tools/compact/core.js';
import { ReflexClient } from './tools/reflex/core.js';
import { TracesClient } from './tools/traces/core.js';
import { OpenAIRouter, AnthropicRouter, GeminiRouter, RawRouter, RouterClassifier } from './modelrouter/core.js';
import { OpenAIToolFactory, AnthropicToolFactory, VercelToolFactory } from './factories/index.js';

/**
 * Configuration for the MorphClient
 */
export interface MorphClientConfig {
  /** Morph API key for authentication (defaults to MORPH_API_KEY env var) */
  apiKey?: string;
  /** Enable debug logging across all tools */
  debug?: boolean;
  /** Default timeout in milliseconds for API requests */
  timeout?: number;
  /** Retry configuration for failed requests */
  retryConfig?: RetryConfig;
  /** GitHub-specific configuration */
  github?: {
    /** Default installation ID for GitHub operations */
    installationId?: string;
  };
}

/**
 * Unified Morph SDK Client
 * 
 * Provides access to all Morph tools through a single interface:
 * - fastApply: AI-powered file editing with intelligent merging
 * - codebaseSearch: Semantic code search
 * - warpGrep: Fast code search with ripgrep
 * - browser: AI-powered browser automation
 * - git: Version control operations
 * - routers: Intelligent model selection (OpenAI, Anthropic, Gemini)
 * - openai/anthropic/vercel: Tool factories for agent frameworks
 */
export class MorphClient extends MorphAPIClient {
  /** Client configuration */
  public config: MorphClientConfig;

  /** FastApply tool for editing files with AI-powered merge */
  public fastApply: FastApplyClient;

  /** CodebaseSearch tool for semantic code search */
  public codebaseSearch: CodebaseSearchClient;

  /** WarpGrep tool for fast code search using ripgrep */
  public warpGrep: WarpGrepClient;

  /** Browser tool for AI-powered browser automation */
  public browser: BrowserClient;

  /** GitHub tool for PR context, comments, and check runs */
  public github: GitHubClient;

  /** Git tool for version control operations */
  public git: MorphGit;

  /** Model routers for intelligent model selection */
  public routers: {
    openai: OpenAIRouter;
    anthropic: AnthropicRouter;
    gemini: GeminiRouter;
    raw: RawRouter;
    classify: RouterClassifier;
  };

  /** OpenAI-compatible tool factories */
  public openai: OpenAIToolFactory;

  /** Anthropic-compatible tool factories */
  public anthropic: AnthropicToolFactory;

  /** Vercel AI SDK tool factories */
  public vercel: VercelToolFactory;

  /** Compact context with line ranges */
  public compact: CompactClient['compact'];

  /** Reflex: train and serve small text classifiers */
  public reflex: ReflexClient;

  /** Traces: read back turns shipped with `morphTracing()`, with their Reflex labels */
  public traces: TracesClient;

  /**
   * Create a new Morph SDK client
   * 
   * @param config - Client configuration (apiKey, debug, timeout, retryConfig)
   * 
   * @example
   * ```typescript
   * const morph = new MorphClient({ 
   *   apiKey: process.env.MORPH_API_KEY,
   *   debug: true,
   *   timeout: 60000
   * });
   * ```
   */
  constructor(config: MorphClientConfig = {}) {
    // MorphClient *is* the transport: it owns auth/host/retries/timeout and every
    // resource below shares it by reference (the OpenAI SDK pattern).
    super({
      apiKey: config.apiKey,
      timeout: config.timeout,
      retryConfig: config.retryConfig,
      debug: config.debug,
    });

    // An explicit key is required (sub-resources like Git depend on it); this
    // preserves the long-standing eager-throw on construction.
    if (!config.apiKey) {
      throw new Error('Morph API key is required. Pass it as { apiKey } when constructing MorphClient.');
    }

    this.config = config;
    logger.debug('MorphClient', 'initialized', { debug: !!config.debug, timeout: config.timeout });

    // Attach every resource, sharing this single transport.
    this.fastApply = new FastApplyClient(this);
    this.codebaseSearch = new CodebaseSearchClient(this);
    this.warpGrep = new WarpGrepClient(this);
    this.browser = new BrowserClient(this);
    this.github = new GitHubClient(this, { installationId: config.github?.installationId });
    this.git = new MorphGit(this);

    this.routers = {
      openai: new OpenAIRouter(this),
      anthropic: new AnthropicRouter(this),
      gemini: new GeminiRouter(this),
      raw: new RawRouter(this),
      classify: new RouterClassifier(this),
    };

    const compactClient = new CompactClient(this);
    this.compact = compactClient.compact.bind(compactClient);

    this.reflex = new ReflexClient(this);
    this.traces = new TracesClient(this);

    // Tool factories build framework-specific tool definitions from the same config.
    this.openai = new OpenAIToolFactory(config);
    this.anthropic = new AnthropicToolFactory(config);
    this.vercel = new VercelToolFactory(config);
  }
}
